/**
 * Calculates viewport-aware cropping coordinates for video capture
 * Handles object-fit: cover logic
 * 
 * This ensures the captured image matches what the user sees in the viewport,
 * accounting for video aspect ratio differences.
 * 
 * @param videoWidth - Natural width of the video stream
 * @param videoHeight - Natural height of the video stream
 * @param viewportWidth - Width of the viewport (defaults to window width)
 * @param viewportHeight - Height of the viewport (defaults to window height)
 * @returns Crop coordinates for canvas.drawImage()
 */
export const calculateVideoCropCoordinates = (
  videoWidth: number,
  videoHeight: number,
  viewportWidth: number = window.innerWidth,
  viewportHeight: number = window.innerHeight
): { sx: number; sy: number; sWidth: number; sHeight: number } => {
  const viewportAspect = viewportWidth / viewportHeight;
  const videoAspect = videoWidth / videoHeight;

  let sx, sy, sWidth, sHeight;

  if (videoAspect > viewportAspect) {
    // Video is wider than viewport - crop sides
    sHeight = videoHeight;
    sWidth = videoHeight * viewportAspect;
    sx = (videoWidth - sWidth) / 2;
    sy = 0;
  } else {
    // Video is taller than viewport - crop top/bottom
    sWidth = videoWidth;
    sHeight = videoWidth / viewportAspect;
    sx = 0;
    sy = (videoHeight - sHeight) / 2;
  }

  return { sx, sy, sWidth, sHeight };
};

/**
 * Captures a frame from a video element as a File object
 * 
 * This function:
 * 1. Calculates the visible portion of the video (matching viewport)
 * 2. Draws it to a canvas
 * 3. Converts to a JPEG blob
 * 4. Returns as a File object ready for upload
 * 
 * @param videoElement - The video element to capture from
 * @param quality - JPEG quality (0-1), defaults to 0.95 for high quality
 * @returns Promise resolving to captured image File
 * @throws Error if canvas context cannot be created or capture fails
 */
export const captureVideoFrame = async (
  videoElement: HTMLVideoElement,
  quality: number = 0.95
): Promise<File> => {
  const vw = videoElement.videoWidth;
  const vh = videoElement.videoHeight;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const { sx, sy, sWidth, sHeight } = calculateVideoCropCoordinates(
    vw,
    vh,
    viewportWidth,
    viewportHeight
  );

  const canvas = document.createElement("canvas");
  canvas.width = viewportWidth;
  canvas.height = viewportHeight;
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Could not get canvas context");
  }

  ctx.drawImage(
    videoElement,
    sx,
    sy,
    sWidth,
    sHeight,
    0,
    0,
    viewportWidth,
    viewportHeight
  );

  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Capture failed"))),
      "image/jpeg",
      quality
    );
  });

  return new File([blob], `frame-${Date.now()}.jpg`, {
    type: blob.type,
  });
};